/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Deliverability", description = "Authenticate your sending domain, ramp volume, and watch the metrics mailbox providers judge you against.", lastUpdatedDate = new Date(1786492800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Deliverability",
        description: "Authenticate your sending domain, ramp volume, and watch the metrics mailbox providers judge you against.",
        lastUpdatedDate: "Wed Aug 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Before your first send",
          "depth": 0,
          "id": "before-your-first-send"
        }, {
          "value": "Ramping up your volume",
          "depth": 0,
          "id": "ramping-up-your-volume"
        }, {
          "value": "What to watch after you launch",
          "depth": 0,
          "id": "what-to-watch-after-you-launch"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      h2: "h2",
      ul: "ul",
      li: "li",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, lifecycle and CRM teams"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Deliverability is whether your email reaches the inbox instead of the spam folder. Your Hightouch team sets up authentication and a volume ramp with you before your first send, and you keep an eye on a few numbers after that."
        })
      }), "\n", _jsx(_components.h2, {
        id: "before-your-first-send",
        children: "Before your first send"
      }), "\n", _jsx(_components.p, {
        children: "Mailbox providers require every bulk sender to authenticate their mail. Hightouch gives you DNS records to add for your sending domain, covering SPF, DKIM, and DMARC, and verifies them before you send. Gmail, Yahoo, and Microsoft all reject or spam-folder unauthenticated bulk mail. This isn't optional."
      }), "\n", _jsx(_components.p, {
        children: "Bring your suppression lists from your current platform at the same time. Sending to addresses that already bounced or complained is the fastest way to damage a new sending reputation."
      }), "\n", _jsx(_components.h2, {
        id: "ramping-up-your-volume",
        children: "Ramping up your volume"
      }), "\n", _jsx(_components.p, {
        children: "Mailbox providers treat a sudden surge from a new sender as a spam signal. A new sending domain needs to build volume gradually, over roughly three to four weeks, starting with your most engaged recipients and widening as your reputation establishes."
      }), "\n", _jsx(_components.p, {
        children: "Your Hightouch team plans that ramp with you. Following it is up to you: you control volume through the audience you attach to each campaign and the schedule you give it."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Nothing in Native Delivery caps your sending volume or holds you to a ramp. If you attach your full list to a campaign in week one, it sends. Build the ramp into your audiences and schedule."
        })
      }), "\n", _jsx(_components.p, {
        children: "A typical ramp widens the audience each stage:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Stage"
            }), _jsx(_components.th, {
              children: "Audience"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Start"
            }), _jsx(_components.td, {
              children: "Recipients who opened or clicked in the last 30 days"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Then"
            }), _jsx(_components.td, {
              children: "Recipients who opened in the last 60 days"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Then"
            }), _jsx(_components.td, {
              children: "Recipients who opened in the last 90 days"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Full volume"
            }), _jsx(_components.td, {
              children: "Everyone subscribed"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Sending to your most engaged recipients first is what makes the ramp work. They open and click, which tells mailbox providers your mail is wanted."
      }), "\n", _jsxs(_components.p, {
        children: ["Two controls help you hold the line. ", _jsx(_components.strong, {
          children: "Limit send rate"
        }), " on a message spreads a single send over time. A workspace-wide daily limit under ", _jsx(_components.strong, {
          children: "Settings → Frequency capping"
        }), " caps how much goes out on any one day."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "A team that needs to send a million messages a day should plan three to five weeks before reaching full volume. Tell your Hightouch team about any launch dates or large sends so the ramp can be planned around them."
        })
      }), "\n", _jsx(_components.h2, {
        id: "what-to-watch-after-you-launch",
        children: "What to watch after you launch"
      }), "\n", _jsx(_components.p, {
        children: "These are the industry thresholds mailbox providers judge you against."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Metric"
            }), _jsx(_components.th, {
              children: "Healthy"
            }), _jsx(_components.th, {
              children: "Investigate"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Spam complaints"
            }), _jsx(_components.td, {
              children: "Under 0.1%"
            }), _jsx(_components.td, {
              children: "Over 0.3%, which providers enforce"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Hard bounces"
            }), _jsx(_components.td, {
              children: "Under 0.5%"
            }), _jsx(_components.td, {
              children: "Over 2%"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Delivery rate"
            }), _jsx(_components.td, {
              children: "Above 98%"
            }), _jsx(_components.td, {
              children: "Below 95%"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Click rate"
            }), _jsx(_components.td, {
              children: "Above 1%"
            }), _jsx(_components.td, {
              children: "Below 0.5%"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Spam complaints are the number that matters most, because providers act on it directly. A rising complaint rate usually means your audience is too broad or your sending too frequent."
      }), "\n", _jsx(_components.p, {
        children: "Click rate is the most reliable engagement signal. Mail privacy features load images without a person reading anything, which inflates open rates. Use opens as a directional hint and trust clicks."
      }), "\n", _jsx(_components.p, {
        children: "A high bounce rate points at list quality, often old or purchased addresses."
      }), "\n", _jsx(_components.p, {
        children: "Check these numbers yourself after each of your first sends. If a rate moves in the wrong direction, slow down and tell your Hightouch team before your next send."
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/subscriptions-and-consent",
            children: "Subscriptions and consent"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/reporting",
            children: "Reporting"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
